/**
 * CR5a — Workbench capability preview bridge (shared contract).
 *
 * Maps CR4 `CapabilityDecision` results onto Workbench action UI state
 * (forbidden · tooltip · suggestedAction). W1 rule-table remains SSOT for
 * which actions exist; capability layer is additive.
 *
 * @see doc/system-completion-plan/01-target/CAPABILITY_RESOLVER_SSOT.md §6
 */

import {
  allowDecision,
  denyDecision,
  CapabilityDenyReason,
  type CapabilityDecision,
  type CapabilitySuggestedAction,
} from '@shared/capability';
import { createPermissionString, type BusinessDomain } from '@shared/types/business-domain';
import type { PathPlane } from '@shared/tenant-host/pathPlanes';
import type { WorkbenchAction } from './action-types';

/** Single preview check — mirrors CR4 batch-resolve context (minus tenant/entity auth). */
export interface CapabilityPreviewInput {
  action: string;
  domain: string;
  objectId?: string;
  fieldKey?: string;
  taskExecutionId?: string;
  flowExecutionId?: string;
  operationExecutionId?: string;
  virtualSpaceId?: string;
  objectLifecycleStatus?: string;
  objectIsArchived?: boolean;
  objectIsDraft?: boolean;
  objectRequiresApproval?: boolean;
  /** Pairing key for resolveActionsFromDecisions — defaults to WorkbenchAction.id */
  previewKey?: string;
}

/** Capability overlay merged onto a Workbench action row/header control. */
export interface WorkbenchActionCapabilityState {
  forbidden?: boolean;
  tooltip?: string;
  suggestedAction?: CapabilitySuggestedAction;
  label?: string;
}

/** Shared object/domain scope when building preview contexts from actions. */
export interface CapabilityPreviewScope {
  domain: string;
  objectId?: string;
  fieldKey?: string;
  taskExecutionId?: string;
  flowExecutionId?: string;
  operationExecutionId?: string;
  virtualSpaceId?: string;
  objectLifecycleStatus?: string;
  objectIsArchived?: boolean;
  objectIsDraft?: boolean;
  objectRequiresApproval?: boolean;
}

/** Workbench action plus scope for batch preview context assembly. */
export interface CapabilityPreviewActionSource {
  workbenchAction: WorkbenchAction;
  scope: CapabilityPreviewScope;
  /** Override capability verb — defaults from workbenchAction.permissionKey */
  capabilityAction?: string;
  previewKey?: string;
}

function capabilityVerbFromHandlerRef(action: WorkbenchAction): string | undefined {
  const handlerPrefix = action.handlerRef.split(':')[0];
  if (handlerPrefix === 'crud' && action.handlerRef.includes(':')) {
    const verb = action.handlerRef.split(':')[1];
    if (verb === 'view') {
      return 'read';
    }
    if (verb === 'restore') {
      return 'update';
    }
    return verb;
  }
  return undefined;
}

/** Resolves CR4 preview domain+verb — honors `domain:verb` permissionKey overrides. */
function resolvePreviewCapability(
  workbenchAction: WorkbenchAction,
  scope: CapabilityPreviewScope,
  capabilityAction?: string
): { domain: string; action: string } | undefined {
  if (capabilityAction) {
    return { domain: scope.domain, action: capabilityAction };
  }
  if (workbenchAction.permissionKey) {
    const key = workbenchAction.permissionKey;
    const colon = key.indexOf(':');
    if (colon > 0) {
      return {
        domain: key.slice(0, colon),
        action: key.slice(colon + 1),
      };
    }
    return { domain: scope.domain, action: key };
  }
  const verb = capabilityVerbFromHandlerRef(workbenchAction);
  if (!verb) {
    return undefined;
  }
  return { domain: scope.domain, action: verb };
}

function requiredPermissionKey(domain: string, action: string): string {
  if (action === 'create' || action === 'read' || action === 'update' || action === 'delete') {
    return createPermissionString(domain as BusinessDomain, action);
  }
  return `${domain}:${action}`;
}

function isMutateCapabilityAction(action: string): boolean {
  return (
    action === 'create' ||
    action === 'update' ||
    action === 'delete' ||
    action === 'approve' ||
    action === 'reject' ||
    action === 'complete' ||
    action === 'execute'
  );
}

function hasDomainPermission(
  domain: string,
  action: string,
  permissionKeys: readonly string[]
): boolean {
  if (permissionKeys.length === 0) {
    return !isMutateCapabilityAction(action);
  }
  const required = requiredPermissionKey(domain, action);
  return permissionKeys.some((key) => key === required || key.endsWith(`:${action}`));
}

/**
 * Maps a `CapabilityDecision` onto workbench action UI state.
 * Denied → forbidden + operator tooltip; allowed → clears forbidden overlay.
 */
export function mergeCapabilityIntoActionState<T extends WorkbenchActionCapabilityState>(
  decision: CapabilityDecision,
  actionState: T
): T {
  if (decision.allowed) {
    return {
      ...actionState,
      forbidden: false,
      tooltip: undefined,
      suggestedAction: undefined,
    };
  }

  return {
    ...actionState,
    forbidden: true,
    tooltip: decision.reason,
    suggestedAction: decision.suggestedAction,
  };
}

function pairDecisionsByKey(
  decisions: readonly CapabilityDecision[],
  previewInputs: readonly CapabilityPreviewInput[]
): Map<string, CapabilityDecision> {
  const map = new Map<string, CapabilityDecision>();
  const count = Math.min(decisions.length, previewInputs.length);
  for (let i = 0; i < count; i += 1) {
    const input = previewInputs[i];
    const decision = decisions[i];
    if (!input || !decision) continue;
    const key = input.previewKey ?? String(i);
    map.set(key, decision);
  }
  return map;
}

/**
 * Applies capability decisions onto resolved actions — pairs by previewKey or index.
 */
export function resolveActionsFromDecisions<
  T extends WorkbenchAction & WorkbenchActionCapabilityState,
>(
  actions: readonly T[],
  decisions: readonly CapabilityDecision[],
  previewInputs?: readonly CapabilityPreviewInput[]
): T[] {
  if (decisions.length === 0) {
    return [...actions];
  }

  let decisionByKey: Map<string, CapabilityDecision> | undefined;

  if (previewInputs && previewInputs.length > 0) {
    decisionByKey = pairDecisionsByKey(decisions, previewInputs);
  } else if (actions.length === decisions.length) {
    decisionByKey = new Map(
      actions.flatMap((action, index) => {
        const decision = decisions[index];
        return decision ? ([[action.id, decision]] as const) : [];
      })
    );
  }

  return actions.map((action, index) => {
    const decision =
      decisionByKey?.get(action.id) ??
      decisionByKey?.get(String(index)) ??
      (actions.length === decisions.length ? decisions[index] : undefined);

    if (!decision) {
      return action;
    }
    return mergeCapabilityIntoActionState(decision, action);
  });
}

/**
 * Builds CR4 batch-resolve contexts from resolved Workbench actions.
 */
export function buildCapabilityPreviewContexts(
  actions: readonly CapabilityPreviewActionSource[]
): CapabilityPreviewInput[] {
  return actions.flatMap(({ workbenchAction, scope, capabilityAction, previewKey }) => {
    const capability = resolvePreviewCapability(workbenchAction, scope, capabilityAction);
    if (!capability) {
      return [];
    }

    return [
      {
        action: capability.action,
        domain: capability.domain,
        ...(scope.objectId !== undefined ? { objectId: scope.objectId } : {}),
        ...(scope.fieldKey !== undefined ? { fieldKey: scope.fieldKey } : {}),
        ...(scope.taskExecutionId !== undefined ? { taskExecutionId: scope.taskExecutionId } : {}),
        ...(scope.flowExecutionId !== undefined ? { flowExecutionId: scope.flowExecutionId } : {}),
        ...(scope.operationExecutionId !== undefined
          ? { operationExecutionId: scope.operationExecutionId }
          : {}),
        ...(scope.virtualSpaceId !== undefined ? { virtualSpaceId: scope.virtualSpaceId } : {}),
        ...(scope.objectLifecycleStatus !== undefined
          ? { objectLifecycleStatus: scope.objectLifecycleStatus }
          : {}),
        ...(scope.objectIsArchived !== undefined
          ? { objectIsArchived: scope.objectIsArchived }
          : {}),
        ...(scope.objectIsDraft !== undefined ? { objectIsDraft: scope.objectIsDraft } : {}),
        ...(scope.objectRequiresApproval !== undefined
          ? { objectRequiresApproval: scope.objectRequiresApproval }
          : {}),
        previewKey: previewKey ?? workbenchAction.id,
      },
    ];
  });
}

/** Maps preview inputs to CR4 batch-resolve request bodies (flow scope forwarded). */
export function previewInputsToBatchContexts(
  previewInputs: readonly CapabilityPreviewInput[],
  plane: PathPlane = 'business'
): Array<{
  action: string;
  domain: string;
  plane: PathPlane;
  objectId?: string;
  fieldKey?: string;
  taskExecutionId?: string;
  flowExecutionId?: string;
  operationExecutionId?: string;
  virtualSpaceId?: string;
  objectLifecycleStatus?: string;
  objectIsArchived?: boolean;
  objectIsDraft?: boolean;
  objectRequiresApproval?: boolean;
}> {
  return previewInputs.map((input) => ({
    action: input.action,
    domain: input.domain,
    plane,
    ...(input.objectId !== undefined ? { objectId: input.objectId } : {}),
    ...(input.fieldKey !== undefined ? { fieldKey: input.fieldKey } : {}),
    ...(input.taskExecutionId !== undefined ? { taskExecutionId: input.taskExecutionId } : {}),
    ...(input.flowExecutionId !== undefined ? { flowExecutionId: input.flowExecutionId } : {}),
    ...(input.operationExecutionId !== undefined
      ? { operationExecutionId: input.operationExecutionId }
      : {}),
    ...(input.virtualSpaceId !== undefined ? { virtualSpaceId: input.virtualSpaceId } : {}),
    ...(input.objectLifecycleStatus !== undefined
      ? { objectLifecycleStatus: input.objectLifecycleStatus }
      : {}),
    ...(input.objectIsArchived !== undefined ? { objectIsArchived: input.objectIsArchived } : {}),
    ...(input.objectIsDraft !== undefined ? { objectIsDraft: input.objectIsDraft } : {}),
    ...(input.objectRequiresApproval !== undefined
      ? { objectRequiresApproval: input.objectRequiresApproval }
      : {}),
  }));
}

/**
 * Local fallback when POST /api/capability/batch-resolve is unavailable.
 *
 * Derives allow/deny from JWT/domain permission strings using the same suffix
 * match as W1 `action-resolver` (`domain:verb` or `*:verb`) — backward compat
 * until the CR4 preview API is wired on the client.
 */
export function resolveCapabilityPreviewLocal(
  inputs: readonly CapabilityPreviewInput[],
  permissionKeys: readonly string[]
): CapabilityDecision[] {
  return inputs.map((input) => {
    const allowed = hasDomainPermission(input.domain, input.action, permissionKeys);
    if (allowed) {
      return allowDecision('domain');
    }

    return denyDecision({
      source: 'domain',
      requiredPermissions: [requiredPermissionKey(input.domain, input.action)],
      reason: '도메인 권한이 없습니다',
      denyReason: CapabilityDenyReason.DomainDenied,
      suggestedAction: 'request_access',
    });
  });
}
